import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Which configuration gap the pill is calling out.
 * - "contributions": plan_contributions has no row for this plan
 * - "coverage": life/disability plan has no row in life_disability_coverage_config (no benefit amount)
 * - "both": both gaps present
 */
export type NeedsConfigReason = "contributions" | "coverage" | "both";

interface NeedsConfigPillProps {
  className?: string;
  /** Determines the tooltip copy. Defaults to "contributions". */
  reason?: NeedsConfigReason;
  /** Override the tooltip text entirely. */
  title?: string;
}

const TOOLTIP_BY_REASON: Record<NeedsConfigReason, string> = {
  contributions:
    'Employer contributions are not configured for this plan. ' +
    'Go to the plan\'s "Rates & Contributions" tab to set up contribution percentages or amounts.',
  coverage:
    'This life/disability plan has no coverage amount configured. ' +
    'Go to the plan\'s coverage settings to set benefit amounts before enrolling employees.',
  both:
    'This plan is missing both employer contributions and life/disability coverage configuration. ' +
    'Configure both on the plan detail before enrolling employees.',
};

/**
 * Amber pill rendered next to plan name on benefits cards / plan-picker rows
 * when the underlying plan isn't fully set up for enrollment. Replaces the
 * separate inline "Coverage not configured" warning so brokers see one signal.
 */
export function NeedsConfigPill({ className, reason = "contributions", title }: NeedsConfigPillProps) {
  return (
    <span
      title={title ?? TOOLTIP_BY_REASON[reason]}
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold whitespace-nowrap",
        "bg-amber-50 text-amber-800 border border-amber-200",
        "dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800/60",
        className,
      )}
    >
      <AlertTriangle className="h-3 w-3" />
      Needs configuration
    </span>
  );
}
